import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram, Award, ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [36, -36]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden">
      <div
        className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-[#E8C2CB]/25 blur-3xl"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-14 items-center min-h-[92vh] pt-32 pb-16">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            data-testid="hero-eyebrow"
            className="text-xs uppercase tracking-[0.26em] font-semibold text-[#70435F] flex items-center gap-3 mb-7"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <span className="inline-block w-10 h-px bg-[#B89A69]" aria-hidden="true" />
            Pediatrician · MBBS | DCH | DNB Pediatrics
          </motion.p>

          <motion.h1
            data-testid="hero-doctor-name"
            className="font-display font-semibold text-[#383237] whitespace-normal lg:whitespace-nowrap leading-tight mb-5"
            style={{ fontSize: "clamp(1.5rem, 3vw, 2.35rem)" }}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.22 }}
          >
            Dr. Ojaswini Sheshrao Gajbhiye
          </motion.h1>

          <h2
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#383237] leading-[1.08] mb-7"
          >
            <MaskedLine delay={0.35}>Every child deserves</MaskedLine>
            <MaskedLine delay={0.5}>
              a <em className="italic text-[#70435F]">healthier beginning.</em>
            </MaskedLine>
          </h2>

          <motion.p
            data-testid="hero-description"
            className="text-base sm:text-lg text-[#695E67] leading-relaxed max-w-xl mb-9"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            A pediatrician with over 8 years of experience and clinical training in Pediatrics and
            Neonatology, dedicated to child health, preventive care, and empowering parents through
            reliable health education.
          </motion.p>

          <motion.div
            className="flex flex-wrap items-center gap-4 mb-8"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToId("education")}
              className="group inline-flex items-center gap-2 bg-[#70435F] text-white px-7 py-3.5 rounded-full hover:bg-[#5A344C] transition-colors shadow-soft font-medium text-sm sm:text-base"
            >
              Explore My Professional Journey
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("little-ones")}
              className="border border-[#70435F]/35 text-[#70435F] px-7 py-3.5 rounded-full hover:bg-[#E8C2CB]/25 hover:text-[#5A344C] transition-colors font-medium text-sm sm:text-base"
            >
              Child Health Education
            </button>
          </motion.div>

          <motion.a
            data-testid="hero-instagram-link"
            href="https://www.instagram.com/littleonesdrojaswini/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-[#8A7E88] hover:text-[#70435F] transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <Instagram size={16} strokeWidth={1.7} />
            @littleonesdrojaswini
          </motion.a>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            className="relative max-w-md mx-auto"
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className="absolute -bottom-6 -right-6 w-full h-full rounded-[30px] bg-[#E8C2CB] border border-[#D6A3AF]/40"
              aria-hidden="true"
            />
            <div
              className="absolute -top-8 -left-8 w-28 h-28 rounded-full border border-[#B89A69]/45"
              aria-hidden="true"
            />
            <motion.div style={{ y: yImg }} className="relative">
              <div className="rounded-[26px] overflow-hidden border-[6px] border-white shadow-soft bg-white">
                <img
                  data-testid="hero-portrait-image"
                  src="/images/portrait.webp"
                  alt="Dr. Ojaswini Sheshrao Gajbhiye, Pediatrician — MBBS, DCH, DNB Pediatrics"
                  className="w-full aspect-[1223/1286] object-cover"
                  width="1223"
                  height="1286"
                  fetchpriority="high"
                />
              </div>
            </motion.div>
            <motion.div
              data-testid="hero-credential-badge"
              style={{ y: yBadge }}
              className="absolute -left-4 sm:-left-10 bottom-10 bg-[#FAF7F4]/95 backdrop-blur border border-[#E7DED4] rounded-2xl px-5 py-4 shadow-card animate-float-slow"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[#70435F]/10 flex items-center justify-center">
                  <Award size={19} className="text-[#B89A69]" strokeWidth={1.7} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-[#383237]">Indian Association of Pediatrics</span>
                  <span className="block text-xs text-[#8A7E88]">Member since 2022</span>
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
